import {useState} from 'react'
import {useTranslate} from 'src/common/i18n/useTranslate'
import {Icon} from 'src/common/Icon'

export type CodeBlockProps = {
  lines: string[]
}

type CopyStatus = 'idle' | 'copied' | 'failed'

export const CodeBlock = ({lines}: CodeBlockProps) => {
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle')
  const translate = useTranslate()

  const handleCopy = () => {
    const showFailed = () => {
      setCopyStatus('failed')
      setTimeout(() => setCopyStatus('idle'), 2500)
    }

    if (!navigator.clipboard) {
      showFailed()
      return
    }

    navigator.clipboard
      .writeText(lines.join('\n'))
      .then(() => {
        setCopyStatus('copied')
        setTimeout(() => setCopyStatus('idle'), 1500)
      })
      .catch(showFailed)
  }

  const copyLabels: Record<CopyStatus, string> = {
    idle: translate('discoveryBlock.copyToClipboard'),
    copied: translate('discoveryBlock.copied'),
    failed: translate('discoveryBlock.copyFailed'),
  }

  return (
    <div className="relative overflow-x-auto rounded-xl border border-green-neon/[18%] bg-green-code p-6">
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copyLabels[copyStatus]}
        className="absolute right-4 top-4 z-10 shrink-0 text-green-mist-dim transition-colors hover:text-green-mist"
      >
        <Icon name={copyStatus === 'copied' ? 'check' : 'copy'} className="size-4" />
      </button>
      {copyStatus === 'failed' && (
        <p role="status" className="m-0 mb-3 pr-8 text-12 text-green-mist-dim">
          {translate('discoveryBlock.copyFailedMessage')}
        </p>
      )}
      <pre className="font-mono m-0 whitespace-pre-wrap break-words pr-8 text-[12.5px] leading-[1.7] text-green-code-text">
        {lines.map((line, lineIndex) => (
          <div key={lineIndex} className="flex gap-3">
            <span className="shrink-0 select-none text-[#4a6b57]">{lineIndex + 1}</span>
            <code>{line}</code>
          </div>
        ))}
      </pre>
    </div>
  )
}
